import { deleteHttp, post } from "../api/apiShared.ts";
import { TaskRelationViewModel } from "../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.types.ts";
import { EntityType } from "../Common.types.ts";
import { RelationSaveModel } from "../../taskManagement/createTaskPane/CreateTaskPane.types.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useOptimisticUpdate } from "@common/hooks/useOptimisticUpdate.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";

export const getRelationsUrl = (entityId: string) => `/relations/${entityId}/`;
export function useRelationsQuery(entityId: string) {
    // An entity that is not created yet, like the one rendered by the template editor preview, has no relations to load.
    const { isLoading, data, refetch } = useDotLegalQuery<Array<TaskRelationViewModel>>(getRelationsUrl(entityId), {
        enabled: !isNullOrWhitespace(entityId),
    });
    return { isLoading, data, refetch };
}

export function useRemoveRelation(entityId: string, relations: Array<TaskRelationViewModel> | undefined) {
    const optimisticUpdate = useOptimisticUpdate();

    async function removeRelation(relationId: string) {
        const remainingRelations = relations!.filter((relation) => relation.relationId !== relationId);
        await optimisticUpdate.putOnQueueAndSetQueryData(remainingRelations, getRelationsUrl(entityId), deleteRelation, relationId);
    }

    return { removeRelation };
}

function deleteRelation(relationId: string) {
    return deleteHttp(`/relations/${relationId}`);
}

export function useRelationHook(entityFromId: string, entityFromType: EntityType, onRelatedAdded?: () => void) {
    const saveMutation = useDotLegalMutation(addRelation);

    function addRelation(saveModel: RelationSaveModel) {
        return post("/relations/add", saveModel);
    }

    const onSave = (saveModel: RelationSaveModel) => {
        if (onRelatedAdded) {
            saveMutation.mutateAsync(saveModel, {
                onSuccess: () => {
                    onRelatedAdded();
                },
            });
        } else {
            saveMutation.mutateAsync(saveModel);
        }
    };

    function onItemSelected(toEntityId: string, toEntityType: EntityType) {
        onSave({
            fromEntityId: entityFromId,
            fromEntityType: entityFromType,
            toEntityId: toEntityId,
            toEntityType: toEntityType,
        });
    }

    return { onItemSelected };
}
